import { useEffect, useId, useRef, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { Overlay } from '../../../ds/components';
import { useEscapeKey, useFocusTrap } from '../../../ds/hooks';
import styles from './FrameModal.module.css';

export interface FrameModalProps {
  open: boolean;
  onClose: () => void;
  eyebrow: string;
  title: string;
  width: number;
  /** Top of the scrim: 0 covers the whole wizard page, 60 keeps the topbar of the settings visible. */
  top: number;
  /** Distance of the card from the scrim's top edge (Admin 07: 36). */
  offset: number;
  children: ReactNode;
}

/** The dialog of the onboarding/settings frames over the blurred, darkened page (A2 „Modal/Dialog"). */
export function FrameModal({ open, onClose, eyebrow, title, width, top, offset, children }: FrameModalProps) {
  const panel = useRef<HTMLDivElement>(null);
  const titleId = useId();
  useFocusTrap(panel, open);
  useEscapeKey(onClose, open);
  useEffect(() => {
    if (!open) return;
    const previous = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = previous;
    };
  }, [open]);
  if (!open) return null;
  return createPortal(
    <Overlay top={top} strength="strong" style={{ paddingTop: offset }} onScrimClick={onClose}>
      <div
        ref={panel}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        tabIndex={-1}
        className={styles.panel}
        style={{ width }}
      >
        <div className={styles.head}>
          <div className={styles.headText}>
            <span className={styles.eyebrow}>{eyebrow}</span>
            <h2 id={titleId} className={styles.title}>
              {title}
            </h2>
          </div>
          <button type="button" className={styles.close} aria-label="Schließen" onClick={onClose}>
            ×
          </button>
        </div>
        {children}
      </div>
    </Overlay>,
    document.body,
  );
}
